import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";
import { useHubModuleAdmin } from "@/lib/useHubModuleAdmin";
import { HubSettingsSection } from "@/components/settings/HubSettingsSection";
import { Button } from "@/components/ui/Button";
import { DateInput, Input, Label, TextArea } from "@/components/ui/Input";
import { PageTitleWithHelp, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import {
  MRP_APP_SETTING_INVENTORY_SNAPSHOT_LAST_FULL_SYNC_AT,
  MRP_FORECAST_LOCATION_STACK_ORDER,
  MRP_LOCATIONS,
  type MrpAppSetting,
  type MrpLocation,
} from "@/lib/mrp";
import { NotificationTopicOwnersSettingsSection } from "@/components/NotificationTopicOwnersSettingsSection";
import { ClampForecastSettingsSection } from "./ClampForecastSettingsSection";

const NOTIFICATION_STAFF_USERS_QUERY_KEY = ["notifications", "staff-user-options"] as const;

function fetchNotificationStaffUsers(): Promise<{
  users: Array<{ userId: string; email: string; displayName: string | null }>;
}> {
  return apiFetch("/api/notifications/staff-user-options");
}

const NEXT_COMPONENT_ORDER_DATES_KEY = "next_component_order_date_by_location";
const DEFAULT_VISIBLE_LOCATIONS_KEY = "default_visible_locations";

function formatInventorySnapshotLastFullSyncAt(value: unknown): string {
  if (value == null) {
    return "No full inventory sync recorded yet.";
  }
  const iso = typeof value === "string" ? value.trim() : String(value);
  if (iso === "") {
    return "No full inventory sync recorded yet.";
  }
  const instant = new Date(iso);
  if (Number.isNaN(instant.getTime())) {
    return iso;
  }
  return `${instant.toLocaleString()} (${iso})`;
}

function stringifyValue(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  return JSON.stringify(value, null, 2);
}

function emptyNextOrderDateDraft(): Record<MrpLocation, string> {
  return { sgs: "", suntronic: "", kodiak: "" };
}

/** YYYY-MM-DD strings for `<input type="date">`; invalid or empty stored values become "". */
function parseNextOrderDatesFromSetting(raw: unknown): Record<MrpLocation, string> {
  const out = emptyNextOrderDateDraft();
  if (raw == null || typeof raw !== "object") return out;
  const object = raw as Record<string, unknown>;
  for (const { code } of MRP_LOCATIONS) {
    const value = object[code];
    if (value == null || value === "") continue;
    if (typeof value !== "string") continue;
    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) out[code] = trimmed;
  }
  return out;
}

function nextOrderDatesToPayload(draft: Record<MrpLocation, string>): Record<MrpLocation, string | null> {
  const out: Record<string, string | null> = {};
  for (const { code } of MRP_LOCATIONS) {
    const trimmed = draft[code].trim();
    out[code] = trimmed === "" ? null : trimmed;
  }
  return out as Record<MrpLocation, string | null>;
}

function nextOrderDatesDirty(draft: Record<MrpLocation, string>, persisted: unknown): boolean {
  return (
    JSON.stringify(nextOrderDatesToPayload(draft)) !== JSON.stringify(nextOrderDatesToPayload(parseNextOrderDatesFromSetting(persisted)))
  );
}

function formatNextOrderDateDisplay(value: string): string {
  const trimmed = value.trim();
  if (trimmed === "") return "Not set";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  const [year, month, day] = trimmed.split("-").map((part) => Number.parseInt(part, 10));
  const instant = new Date(Date.UTC(year, month - 1, day));
  if (Number.isNaN(instant.getTime())) return trimmed;
  return instant.toLocaleDateString(undefined, { timeZone: "UTC" });
}

function parseDefaultVisibleLocations(raw: unknown): Set<MrpLocation> {
  const out = new Set<MrpLocation>();
  if (!Array.isArray(raw)) return out;
  for (const item of raw) {
    if (item === "sgs" || item === "suntronic" || item === "kodiak") out.add(item);
  }
  return out;
}

/** Stable order for stored JSON (matches forecast / build plan stack order). */
function defaultVisibleLocationsToPayload(selected: Set<MrpLocation>): MrpLocation[] {
  return MRP_FORECAST_LOCATION_STACK_ORDER.filter((code) => selected.has(code));
}

function defaultVisibleLocationsDirty(draft: Set<MrpLocation>, persisted: unknown): boolean {
  const left = defaultVisibleLocationsToPayload(draft);
  const right = defaultVisibleLocationsToPayload(parseDefaultVisibleLocations(persisted));
  return JSON.stringify(left) !== JSON.stringify(right);
}

export function MrpSettings() {
  const queryClient = useQueryClient();
  const hubMrpSettingsAllowed = useHubModuleAdmin("mrp");
  const mrpHubSettingsAdminTitle = "Module administrator access is required to change MRP hub settings.";
  const settingsQuery = useQuery({
    queryKey: ["mrp", "app-settings"],
    queryFn: () => apiFetch<{ settings: MrpAppSetting[] }>("/api/mrp/app-settings"),
  });

  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [nextOrderDatesDraft, setNextOrderDatesDraft] = useState<Record<MrpLocation, string>>(emptyNextOrderDateDraft);
  const [defaultVisibleDraft, setDefaultVisibleDraft] = useState<Set<MrpLocation>>(() => new Set());

  useEffect(() => {
    if (!settingsQuery.data) return;
    const next: Record<string, string> = {};
    for (const setting of settingsQuery.data.settings) {
      if (setting.key === NEXT_COMPONENT_ORDER_DATES_KEY) {
        setNextOrderDatesDraft(parseNextOrderDatesFromSetting(setting.value));
        continue;
      }
      if (setting.key === DEFAULT_VISIBLE_LOCATIONS_KEY) {
        setDefaultVisibleDraft(parseDefaultVisibleLocations(setting.value));
        continue;
      }
      if (setting.key === MRP_APP_SETTING_INVENTORY_SNAPSHOT_LAST_FULL_SYNC_AT) {
        continue;
      }
      next[setting.key] = stringifyValue(setting.value);
    }
    setDrafts(next);
  }, [settingsQuery.data]);

  const patchMutation = useMutation({
    mutationFn: (input: { key: string; value: unknown }) =>
      apiFetch(`/api/mrp/app-settings/${input.key}`, {
        method: "PATCH",
        body: { value: input.value },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["mrp", "app-settings"] });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "forecast"] });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "clamp-forecast"] });
    },
  });

  return (
    <div className="space-y-4">
      <header>
        <PageTitleWithHelp hoverHint="Application-wide MRP knobs. Each block below is one app setting key. Values are stored as JSON: use valid JSON literals for numbers and arrays. Use Save on each block when you change it.">
          MRP settings
        </PageTitleWithHelp>
      </header>

      <div className="space-y-4">
          {(settingsQuery.data?.settings ?? []).map((setting) => {
            if (setting.key === DEFAULT_VISIBLE_LOCATIONS_KEY) {
              const dirty = defaultVisibleLocationsDirty(defaultVisibleDraft, setting.value);
              return (
                <HubSettingsSection
                  key={setting.key}
                  title={<span className="font-mono text-sm">{setting.key}</span>}
                  hoverHint={setting.notes != null && setting.notes.trim() !== "" ? setting.notes : undefined}
                >
                  <div className="flex flex-wrap gap-2">
                    {MRP_LOCATIONS.map((location) => {
                      const isOn = defaultVisibleDraft.has(location.code);
                      return (
                        <Button
                          key={location.code}
                          size="sm"
                          type="button"
                          variant={isOn ? "primary" : "secondary"}
                          disabled={!hubMrpSettingsAllowed}
                          onClick={() => {
                            setDefaultVisibleDraft((prev) => {
                              const next = new Set(prev);
                              if (next.has(location.code)) next.delete(location.code);
                              else next.add(location.code);
                              return next;
                            });
                          }}
                        >
                          {location.label}
                        </Button>
                      );
                    })}
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-3">
                    <p className="text-xs text-slate-500">
                      Last updated by {setting.updated_by ?? "system"} on{" "}
                      {new Date(setting.updated_at).toLocaleString()}
                    </p>
                    <ReadOnlyHoverLayer
                      active={!hubMrpSettingsAllowed}
                      hoverHint={mrpHubSettingsAdminTitle}
                      layout="inline"
                    >
                      <Button
                        size="sm"
                        disabled={!dirty || !hubMrpSettingsAllowed}
                        isLoading={patchMutation.isPending && patchMutation.variables?.key === setting.key}
                        onClick={() => {
                          patchMutation.mutate({
                            key: setting.key,
                            value: defaultVisibleLocationsToPayload(defaultVisibleDraft),
                          });
                        }}
                      >
                        Save
                      </Button>
                    </ReadOnlyHoverLayer>
                  </div>
                </HubSettingsSection>
              );
            }

            if (setting.key === MRP_APP_SETTING_INVENTORY_SNAPSHOT_LAST_FULL_SYNC_AT) {
              return (
                <HubSettingsSection
                  key={setting.key}
                  title={<span className="font-mono text-sm">{setting.key}</span>}
                  hoverHint={
                    setting.notes != null && setting.notes.trim() !== ""
                      ? setting.notes
                      : "UTC instant when the last successful full live inventory ingest finished. Updated automatically by inventory ingest; not editable here."
                  }
                >
                  <Label>Last full inventory snapshot sync</Label>
                  <p className="mt-2 text-sm text-slate-800">
                    {formatInventorySnapshotLastFullSyncAt(setting.value)}
                  </p>
                  <p className="mt-2 text-xs text-slate-500">
                    Last updated by {setting.updated_by ?? "system"} on{" "}
                    {new Date(setting.updated_at).toLocaleString()}
                  </p>
                </HubSettingsSection>
              );
            }

            if (setting.key === NEXT_COMPONENT_ORDER_DATES_KEY) {
              const dirty = nextOrderDatesDirty(nextOrderDatesDraft, setting.value);
              return (
                <HubSettingsSection
                  key={setting.key}
                  title={<span className="font-mono text-sm">{setting.key}</span>}
                  hoverHint={setting.notes != null && setting.notes.trim() !== "" ? setting.notes : undefined}
                >
                  <ReadOnlyHoverLayer
                    active={!hubMrpSettingsAllowed}
                    hoverHint={mrpHubSettingsAdminTitle}
                    layout="block"
                  >
                    <div className="grid gap-3 sm:grid-cols-3">
                      {MRP_LOCATIONS.map(({ code, label }) => (
                        <div key={code}>
                          <Label htmlFor={`next-component-order-${code}`}>{label}</Label>
                          {hubMrpSettingsAllowed ? (
                            <DateInput
                              id={`next-component-order-${code}`}
                              value={nextOrderDatesDraft[code]}
                              onChange={(event) =>
                                setNextOrderDatesDraft((prev) => ({ ...prev, [code]: event.target.value }))
                              }
                              className="mt-1.5"
                            />
                          ) : (
                            <p
                              id={`next-component-order-${code}`}
                              className="mt-1.5 text-sm text-slate-800"
                            >
                              {formatNextOrderDateDisplay(nextOrderDatesDraft[code])}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </ReadOnlyHoverLayer>
                  <div className="mt-2 flex items-center justify-between gap-3">
                    <p className="text-xs text-slate-500">
                      Last updated by {setting.updated_by ?? "system"} on{" "}
                      {new Date(setting.updated_at).toLocaleString()}
                    </p>
                    <ReadOnlyHoverLayer
                      active={!hubMrpSettingsAllowed}
                      hoverHint={mrpHubSettingsAdminTitle}
                      layout="inline"
                    >
                      <Button
                        size="sm"
                        disabled={!dirty || !hubMrpSettingsAllowed}
                        isLoading={patchMutation.isPending && patchMutation.variables?.key === setting.key}
                        onClick={() => {
                          patchMutation.mutate({
                            key: setting.key,
                            value: nextOrderDatesToPayload(nextOrderDatesDraft),
                          });
                        }}
                      >
                        Save
                      </Button>
                    </ReadOnlyHoverLayer>
                  </div>
                </HubSettingsSection>
              );
            }

            const draft = drafts[setting.key] ?? "";
            const persisted = stringifyValue(setting.value);
            const dirty = draft !== persisted;
            const isShortValue = !persisted.includes("\n") && persisted.length < 80;
            return (
              <HubSettingsSection
                key={setting.key}
                title={<span className="font-mono text-sm">{setting.key}</span>}
                hoverHint={setting.notes != null && setting.notes.trim() !== "" ? setting.notes : undefined}
              >
                <ReadOnlyHoverLayer
                  active={!hubMrpSettingsAllowed}
                  hoverHint={mrpHubSettingsAdminTitle}
                  layout="block"
                >
                  <Label>Value (JSON)</Label>
                  {isShortValue ? (
                    <Input
                      value={draft}
                      disabled={!hubMrpSettingsAllowed}
                      onChange={(event) => setDrafts((prev) => ({ ...prev, [setting.key]: event.target.value }))}
                      className="mt-2 font-mono text-xs"
                    />
                  ) : (
                    <TextArea
                      value={draft}
                      disabled={!hubMrpSettingsAllowed}
                      onChange={(event) => setDrafts((prev) => ({ ...prev, [setting.key]: event.target.value }))}
                      className="mt-2 text-xs"
                    />
                  )}
                </ReadOnlyHoverLayer>
                <div className="mt-2 flex items-center justify-between gap-3">
                  <p className="text-xs text-slate-500">
                    Last updated by {setting.updated_by ?? "system"} on{" "}
                    {new Date(setting.updated_at).toLocaleString()}
                  </p>
                  <ReadOnlyHoverLayer
                    active={!hubMrpSettingsAllowed}
                    hoverHint={mrpHubSettingsAdminTitle}
                    layout="inline"
                  >
                    <Button
                      size="sm"
                      disabled={!dirty || !hubMrpSettingsAllowed}
                      isLoading={patchMutation.isPending && patchMutation.variables?.key === setting.key}
                      onClick={() => {
                        let parsed: unknown;
                        try {
                          parsed = JSON.parse(draft);
                        } catch {
                          parsed = draft;
                        }
                        patchMutation.mutate({ key: setting.key, value: parsed });
                      }}
                    >
                      Save
                    </Button>
                  </ReadOnlyHoverLayer>
                </div>
              </HubSettingsSection>
            );
          })}
          {settingsQuery.data && settingsQuery.data.settings.length === 0 ? (
            <p className="text-sm text-slate-500">No settings have been configured.</p>
          ) : null}
      </div>

      <ClampForecastSettingsSection />

      <NotificationTopicOwnersSettingsSection
        topic="purchase_request"
        title="Purchase & inventory review topic owners"
        hoverHint="Hub users in this topic owner group receive email and in-app notifications when a purchase request or inventory review is created and when someone posts a reply. They may also claim ownership of open items."
        canEdit={hubMrpSettingsAllowed}
        userOptionsQueryKey={NOTIFICATION_STAFF_USERS_QUERY_KEY}
        fetchUserOptions={fetchNotificationStaffUsers}
        readOnlyHoverHint={mrpHubSettingsAdminTitle}
      />

      <NotificationTopicOwnersSettingsSection
        topic="mrp_rop_internal_build"
        title="Reorder point in-house builds"
        hoverHint="Hub users in this distribution group receive in-app and email notifications when a manufactured reorder-point item is released as an in-house work order on Assembly."
        canEdit={hubMrpSettingsAllowed}
        userOptionsQueryKey={NOTIFICATION_STAFF_USERS_QUERY_KEY}
        fetchUserOptions={fetchNotificationStaffUsers}
        readOnlyHoverHint={mrpHubSettingsAdminTitle}
      />

      <NotificationTopicOwnersSettingsSection
        topic="mrp_rfq"
        title="RFQ notification distribution"
        hoverHint="Hub users in this distribution group receive in-app and email notifications when someone outside the group creates a new RFQ draft."
        canEdit={hubMrpSettingsAllowed}
        userOptionsQueryKey={NOTIFICATION_STAFF_USERS_QUERY_KEY}
        fetchUserOptions={fetchNotificationStaffUsers}
        readOnlyHoverHint={mrpHubSettingsAdminTitle}
      />

      {patchMutation.error ? (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
          {(patchMutation.error as Error).message}
        </div>
      ) : null}
    </div>
  );
}
